import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import MenuIcon from '@mui/icons-material/Menu';

export function Navigation() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    { text: 'Features', path: '/features' },
    { text: 'Pricing', path: '/pricing' },
    { text: 'About', path: '/about' },
    { text: 'FAQ', path: '/faq' },
  ];

  const isDark = theme.palette.mode === 'dark';

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={0}
      sx={{
        backgroundColor: isDark ? 'rgba(15,23,42,0.82)' : 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: isDark
          ? [
              '0 4px 24px rgba(0,0,0,0.4)',
              '0 1px 6px rgba(99,102,241,0.15)',
              'inset 0 -1px 0 rgba(255,255,255,0.06)',
            ].join(', ')
          : [
              '0 4px 24px rgba(99,102,241,0.12)',
              '0 1px 6px rgba(0,0,0,0.06)',
              'inset 0 -1px 0 rgba(99,102,241,0.08)',
            ].join(', '),
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(99,102,241,0.1)'}`,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <Box
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              textDecoration: 'none',
              '&:hover .logo-orb': {
                boxShadow: [
                  '0 8px 20px rgba(99,102,241,0.6)',
                  '0 3px 8px rgba(0,0,0,0.18)',
                  'inset 0 2px 0 rgba(255,255,255,0.32)',
                  'inset 0 -2px 0 rgba(0,0,0,0.18)',
                ].join(', '),
                transform: 'scale(1.08)',
              },
            }}
          >
            {/* Clay logo orb */}
            <Box
              className="logo-orb"
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                boxShadow: [
                  '0 6px 16px rgba(99,102,241,0.45)',
                  '0 2px 6px rgba(0,0,0,0.14)',
                  'inset 0 2px 0 rgba(255,255,255,0.3)',
                  'inset 0 -2px 0 rgba(0,0,0,0.16)',
                ].join(', '),
                transition: 'box-shadow 0.22s ease, transform 0.22s ease',
              }}
            >
              <SchoolIcon sx={{ fontSize: 18, color: 'white' }} />
            </Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: 'primary.dark' }}>
              BrainLoop
            </Typography>
          </Box>
          
          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  variant="text"
                  color="inherit"
                  sx={{ mr: 2 }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          )}

          {/* Login Button - Always Visible */}
          <Button
            component={Link}
            to="/login"
            variant="contained"
            color="primary"
            sx={{
              ml: isMobile ? 0 : 2,
              mr: isMobile ? 2 : 0,
              borderRadius: '20px',
              px: 2.5,
              fontWeight: 700,
              background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
              boxShadow: [
                '0 6px 16px rgba(99,102,241,0.45)',
                '0 2px 6px rgba(0,0,0,0.12)',
                'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                'inset 0 -1.5px 0 rgba(0,0,0,0.14)',
              ].join(', '),
              transition: 'transform 0.18s ease, box-shadow 0.18s ease',
              '&:hover': {
                background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.secondary.dark})`,
                boxShadow: [
                  '0 8px 22px rgba(99,102,241,0.55)',
                  '0 3px 8px rgba(0,0,0,0.15)',
                  'inset 0 1.5px 0 rgba(255,255,255,0.25)',
                  'inset 0 -1.5px 0 rgba(0,0,0,0.16)',
                ].join(', '),
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(1px)',
                boxShadow: '0 3px 8px rgba(99,102,241,0.35)',
              },
            }}
          >
            Login
          </Button>

          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={handleMenu}
              sx={{ color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Mobile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              },
            }}
          >
            {menuItems.map((item) => (
              <MenuItem
                key={item.text}
                component={Link}
                to={item.path}
                onClick={handleClose}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {item.text}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
} 